import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useAuth0User } from '@hooks/useAuth0User';
import { APP_VERSIONS } from '@hooks/useIsAppView';
import { BodySmallRegular, H3 } from '@components/atoms/typography';

interface AppLoginButtonProps {
  isAppView: boolean;
  appVersion: string;
  className?: string;
}

const AppLoginButton: React.FC<AppLoginButtonProps> = ({
  isAppView,
  appVersion,
  className = ''
}) => {
  const { user, isLoading } = useAuth0User();
  const router = useRouter();
  const [rememberMe, setRememberMe] = useState(true);

  const handleLoginPress = () => {
    const currentPath = router.asPath;
    window.location.href = `/auth/login?returnTo=${encodeURIComponent(
      currentPath
    )}`;
  };

  if (!isAppView || user || isLoading) {
    return null;
  }

  if (appVersion === APP_VERSIONS.LEGACY) {
    return (
      <div className={`fixed top-6 right-6 z-50  ${className}`}>
        <button
          onClick={handleLoginPress}
          className="flex items-center justify-center gap-2 bg-primary-500/90 hover:bg-primary-600 text-white px-3 py-2 rounded-lg shadow-md text-xs animate-pulse"
          aria-label="Login to access your account"
        >
          <span className="font-medium text-sm">Sign in</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 pointer-events-none ${className}`}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto pointer-events-auto relative z-10">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 pt-8 text-center">
          <div className="w-16 h-16 bg-[#121212] mx-auto mb-4 flex items-center justify-center">
            <img
              src="/images/hub-logo.svg"
              alt="Fantasy Football Hub Logo"
              className="w-16 h-16 border border-black/50"
            />
          </div>
          <H3 className="text-center" style={{ fontSize: '20px' }}>
            Sign in again for quick access to your FFH account
          </H3>
        </div>
        {/* Content */}
        <div className="px-6 py-6">
          <div className="text-center mb-6">
            <BodySmallRegular
              className="text-gray-700"
              style={{ fontSize: '14px' }}
            >
              Enjoy a fast and smooth experience — sign in once more for
              continuous access to all features from here.
            </BodySmallRegular>
          </div>
          {/* Remember Me Checkbox */}
          <div className="mb-6">
            <label className="flex items-center space-x-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 border-2 rounded transition-all duration-200 flex items-center justify-center ${
                    rememberMe
                      ? 'bg-primary-500 border-primary-500'
                      : 'border-gray-300 group-hover:border-primary-400'
                  }`}
                >
                  {rememberMe && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={4}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-700 select-none">
                Remember me for faster sign in
              </span>
            </label>
          </div>

          {/* Login Button */}
          <button
            onClick={handleLoginPress}
            className="w-full flex items-center justify-center gap-2 bg-primary-500/90 hover:bg-primary-600 text-white px-3 py-2 rounded-lg shadow-md text-xs animate-pulse"
            aria-label="Login to access your account"
          >
            <span className="font-medium text-sm">Sign in</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppLoginButton;
